import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { Chart, registerables } from 'chart.js';

import { TimeFrameMode } from 'src/app/shared/interfaces/global-filter-model';
import { MondelezDataService } from 'src/app/shared/services/mondelez-data.service';
import { GlobalFilterService } from 'src/app/shared/services/global-filter.service';

Chart.register(...registerables);

@Component({
  selector: 'app-demand-chart',
  templateUrl: './demand-chart.component.html',
  styleUrls: ['./demand-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DemandChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() demandData: any;
  @Input() showTimeFrameModeToggle = true;

  private yearRange = {
    startYear: undefined,
    endYear: undefined,
  };
  public timeFrameMode: TimeFrameMode;
  public aggregatedData = [];
  public regionKeys: any = [];
  public chartParams = [];
  public chartData = {
    year: {},
    quarter: {},
  };

  private subs = new Subscription();

  constructor(
    private mondelezDataService: MondelezDataService,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.timeFrameMode) {
      this.prepareChart();
      this.aggregatedData = this.aggregator();
    }
  }

  onTimeFrameChange(value: TimeFrameMode) {
    this.mondelezDataService.updateTimeFrameMode(value);
  }

  ngOnInit(): void {
    this.subs.add(
      this.globalFilterService.globalFilters.subscribe((filter) => {
        if (filter) {
          this.yearRange.startYear = filter.start_year;
          this.yearRange.endYear = filter.end_year;
        }
      }),
    );
    this.subs.add(
      this.mondelezDataService.timeFrameMode.subscribe((value) => {
        this.timeFrameMode = value;
        this.prepareChart();
        this.aggregatedData = this.aggregator();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private prepareChart() {
    this.chartParams = [
      {
        name: 'yoy',
        type: 'line',
        label: '% Change YoY',
        color: '#6C4688',
        border: '#6C4688',
        bg: '#EAEAF2',
        stacked: true,
        unit: '%',
        legend: { isTop: true, isRight: false },
      },
    ];
    this.regionKeys = Object.keys(this.demandData.year).filter((ele) => ele != 'total');
    const backgroundColors = ['#2E2E38 ', '#747480', '#C4C4CD', '#E1E1E6'];

    let dataSliceIndex = 0;
    if (this.demandData[this.timeFrameMode]['total'].length > this.getTimeRangeLable().length) {
      dataSliceIndex = 1;
    }

    this.regionKeys.forEach((key, index) => {
      this.chartParams.push({
        name: key,
        type: 'bar',
        label: key + ' Demand',
        color: backgroundColors[index],
        stacked: true,
        titleY: 'Demand (MM lbs)',
      });
      this.chartData[this.timeFrameMode][key] = this.demandData[this.timeFrameMode][key].slice(dataSliceIndex);
    });
    this.chartData[this.timeFrameMode]['yoy'] = this.calculateYoY();
  }

  private calculateYoY() {
    if (this.demandData != undefined) {
      let dataSliceIndex = 0;
      let previous = undefined;
      if (this.demandData[this.timeFrameMode]['total'].length > this.getTimeRangeLable().length) {
        previous = this.demandData[this.timeFrameMode]['total'][0];
        dataSliceIndex = 1;
      }
      return this.demandData[this.timeFrameMode]['total'].slice(dataSliceIndex).map((el) => {
        let per = 0;
        if (previous) {
          per = ((el - previous) * 100) / previous;
        } else {
          per = 0;
        }
        previous = el;
        // return Math.round(per);
        return parseFloat(per.toFixed(2));
      });
    } else {
      return [];
    }
  }

  getTimeRangeLable() {
    const labels = [];
    for (let year = this.yearRange.startYear; year < this.yearRange.endYear + 1; year++) {
      if (this.timeFrameMode == TimeFrameMode.QUARTERLY) {
        for (let q = 1; q <= 4; q++) {
          labels.push(year + '-Q' + q);
        }
      } else {
        labels.push(year);
      }
    }
    return labels;
  }

  private formatNumbers(amt: number) {
    if (!amt) return 0;
    let millionth = amt / 1000000;
    return parseFloat(millionth.toFixed(1));
  }

  private aggregator() {
    if (this.demandData != undefined) {
      const countryList = Object.keys(this.demandData[this.timeFrameMode]).filter((el) => el != 'total');
      const aggData = countryList.map((el) => {
        const sum = this.demandData[this.timeFrameMode][el][this.demandData[this.timeFrameMode][el].length - 1] - this.demandData[this.timeFrameMode][el][0];
        return { label: el, value: this.formatNumbers(sum), prefix: '', suffix: 'MM lbs' };
      });

      aggData.push({ label: 'CAGR', value: this.demandData['cagr'] * 100, prefix: '', suffix: '%' });
      return aggData;
    } else {
      return [];
    }
  }
}
